import type { ClientDeleteOneResponse } from "api-types/common/commonResponses";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { MasterPlansSchemaUpdateOne } from "api-types/masterPlans/masterPlansRequests";
import type {
  ClientMasterPlan,
  ClientMasterPlanHydrated,
  ClientMasterPlanHydratedWithPublishedDate,
} from "api-types/masterPlans/masterPlansResponses";
import type { OptionEffectOverrides } from "building-types/options/optionEffect";
import type { OptionSetOverrides } from "building-types/options/optionTemplateTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import type { UserNotificationType } from "common/types/userNotification";
import { handleFetchError } from "common/utils/fetchHelpers";
import { deleteWithTimeout, getWithTimeout, postWithTimeout, putWithTimeout } from "lib/utils/fetchAuthHelpers";
import type { StorageResponse } from "./types";

export async function updateMasterPlanOverrides(
  builderId: string,
  masterPlanId: string,
  overrides: OptionSetOverrides,
  optionEffectOverrides?: OptionEffectOverrides,
) {
  await putWithTimeout(
    `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}`,
    { overrides, optionEffectOverrides },
    30 * 1000,
  );
}

export async function getMasterPlan(
  builderId: string,
  masterPlanId: string,
): StorageResponse<ClientMasterPlanHydratedWithPublishedDate> {
  try {
    const res = await getWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}`);
    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientMasterPlanHydratedWithPublishedDate, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function getMasterPlanHydrated(
  builderId: string,
  masterPlanId: string,
): StorageResponse<ClientMasterPlanHydrated> {
  try {
    const res = await getWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}/hydrated`,
    );
    const json = await res.json();
    if (!res.ok) {
      throw json;
    }

    return [json as ClientMasterPlanHydrated, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function postMasterPlan(
  builderId: string,
  basePrice: number,
  studioDocId: string,
  name?: string,
  overrides?: OptionSetOverrides,
): StorageResponse<ClientMasterPlan> {
  try {
    const res = await postWithTimeout(`${WEB_API_BASE_PATH}/builders/${builderId}/master-plans`, {
      name,
      overrides,
      basePrice,
      studioDocId,
    });

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientMasterPlan, undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function putMasterPlan(
  builderId: string,
  masterPlanId: string,
  updates: MasterPlansSchemaUpdateOne,
): StorageResponse<ClientMasterPlan> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}`,
      updates,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientMasterPlan, undefined];
  } catch (e) {
    return [undefined, await handleFetchError(e)];
  }
}

export async function deleteMasterPlan(
  builderId: string | undefined,
  masterPlanId: string | undefined,
): Promise<UserNotificationType | undefined> {
  try {
    const res = await deleteWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}`,
      null,
      30000,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return undefined;
  } catch (e) {
    return await handleFetchError(e);
  }
}

export async function archiveMasterPlan(
  builderId: string | undefined,
  masterPlanId: string | undefined,
): StorageResponse<ClientDeleteOneResponse, null> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}/archive`,
      {},
      30000,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientDeleteOneResponse, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function unarchiveMasterPlan(
  builderId: string | undefined,
  masterPlanId: string | undefined,
): StorageResponse<ClientDeleteOneResponse, null> {
  try {
    const res = await putWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}/unarchive`,
      {},
      30000,
    );

    const json = await res.json();

    if (!res.ok) {
      throw json;
    }

    return [json as ClientDeleteOneResponse, null];
  } catch (e) {
    return [null, await handleFetchError(e)];
  }
}

export async function postCommunityPlansBatchForMasterPlan(
  builderId: string,
  masterPlanId: string,
  body: {
    communityIds: string[];
    name?: string;
    publicName?: string;
    includeAncestors?: boolean;
  },
): StorageResponse<ClientLocationPlan[]> {
  try {
    const res = await postWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/${masterPlanId}/location-plans/batch`,
      body,
      60 * 1000,
    );
    const json = await res.json();
    if (!res.ok) {
      throw json;
    }
    return [json as ClientLocationPlan[], undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}

export async function postCommunityPlansBatchMultiPlan(
  builderId: string,
  body: {
    masterPlanIds: string[];
    communityIds: string[];
    name?: string;
    publicName?: string;
    includeAncestors?: boolean;
  },
): StorageResponse<ClientLocationPlan[]> {
  try {
    const res = await postWithTimeout(
      `${WEB_API_BASE_PATH}/builders/${builderId}/master-plans/location-plans/batch-multi-plan`,
      body,
      60 * 1000,
    );
    const json = await res.json();
    if (!res.ok) {
      throw json;
    }
    return [json as ClientLocationPlan[], undefined];
  } catch (e) {
    return [, await handleFetchError(e)];
  }
}
